import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { MathRichText } from "@/lib/mathRichText";
import {
  KEYWORD_TO_CODEPOINT,
  lottieFallbackUrlsForCodepoint,
  notoEmojiLottieUrl,
} from "@/lib/lottieVisual";
import { emojiFallbackForKeyword, resolveNamedIcon } from "@/lib/namedIcons";
import { LottiePlayer } from "@/components/LottiePlayer";

const TERM_BORDER = ["border-pink-400", "border-blue-400", "border-green-400", "border-purple-400"];

const NON_ASCII = /[^\u0000-\u007F]/;

interface Props {
  data: { type: "term"; term: string; definition: string; icon?: string; animation?: string };
  index: number;
  isChildTheme?: boolean;
}

function lottieCodepointForTerm(icon: string | undefined, term: string): string | null {
  const i = icon?.toLowerCase().trim();
  const firstWord = (term || "").split(/\s+/)[0]?.toLowerCase() || "";
  if (i && KEYWORD_TO_CODEPOINT[i]) return KEYWORD_TO_CODEPOINT[i];
  if (firstWord && KEYWORD_TO_CODEPOINT[firstWord]) return KEYWORD_TO_CODEPOINT[firstWord];
  return null;
}

export function ElementTerm({ data, index, isChildTheme }: Props) {
  const border = TERM_BORDER[index % TERM_BORDER.length];
  const child = !!isChildTheme;
  const iconRaw = (data.icon || "").trim();
  const term = data.term || "";
  const cp = lottieCodepointForTerm(data.icon, term);
  const fallbackEmoji = emojiFallbackForKeyword(iconRaw || term.split(/\s+/)[0] || "book");

  const namedFromIcon = resolveNamedIcon(iconRaw, child);
  const namedFromTermWord = resolveNamedIcon(term.split(/\s+/)[0], child);

  let visual: ReactNode = null;

  if (cp) {
    visual = (
      <div className="w-[72px] h-[72px] mb-2 flex items-center justify-center">
        <LottiePlayer
          src={notoEmojiLottieUrl(cp)}
          fallbackSrcs={lottieFallbackUrlsForCodepoint(cp)}
          fallbackEmoji={fallbackEmoji}
          className="w-[72px] h-[72px]"
          aria-label={iconRaw || term}
        />
      </div>
    );
  } else if (child && (namedFromIcon || namedFromTermWord)) {
    visual = (
      <div className="text-5xl mb-2 leading-none" aria-hidden>
        {namedFromIcon || namedFromTermWord}
      </div>
    );
  } else if (iconRaw && NON_ASCII.test(iconRaw)) {
    visual = (
      <div className="text-5xl mb-2 leading-none" aria-hidden>
        {iconRaw}
      </div>
    );
  } else if (!child && iconRaw) {
    visual = (
      <div className="text-5xl mb-2 leading-none font-normal" aria-hidden>
        {iconRaw}
      </div>
    );
  } else if (term) {
    const letter = term.trim().charAt(0).toUpperCase() || "?";
    visual = (
      <div
        className="w-[72px] h-[72px] mb-2 rounded-full bg-gradient-to-br from-violet-600 to-purple-700 text-white text-3xl font-black flex items-center justify-center shadow-lg border-2 border-white/30"
        aria-hidden
      >
        {letter}
      </div>
    );
  }

  return (
    <motion.div
      className={
        child
          ? `rounded-2xl border-4 ${border} bg-white/95 p-4 shadow-xl flex flex-col items-center text-center`
          : `rounded-xl border-4 ${border} bg-white p-4 shadow-md hover:shadow-lg transition-shadow`
      }
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      {visual}
      <dt
        className={
          child ? "font-extrabold text-lg md:text-xl text-slate-900" : "font-bold text-lg text-slate-800"
        }
      >
        <MathRichText text={data.term} />
      </dt>
      <dd className={child ? "text-slate-700 text-sm md:text-base mt-2 leading-snug" : "text-slate-600 text-sm mt-1"}>
        <MathRichText text={data.definition} />
      </dd>
    </motion.div>
  );
}
